/**
 * CONFIDENTIAL AND PROPRIETARY SOURCE CODE.
 *
 * Use and distribution of this code is subject to applicable licenses and the
 * permission of the code owner. This notice does not indicate the actual or
 * intended publication of this source code.
 *
 * Portions developed for Bronner's Christmas by BTM Global Consulting
 * LLC and are the property of Bronner's Christmas.
 * =============================================================================
 * Version   Date          Author          Remarks
 * 1.0       2026-06-12    Tuan Le         Case # 6903611 Update: "CDN enabled causes Listrak head script to load twice, freezing storefront pages"
 * 2.0       2026-10-05    Tuan Le         Ensure Render runs asynchronously after current call stack and after Listrak script is ready
 * 3.0       2026-10-16    Bao Pham        79193 - Prevent CDN from caching transient Listrak JSONP callback scripts (ltkCallbackXXXX)
 */

define('BRListrak.Lib', [
    'Utils',
    'SC.Configuration',
    'jQuery',
    'LiveOrder.Model',
    'LiveOrder.Line.Model',
    'InstrumentationAddToCart',
    'Loggers',
    'Cart.Confirmation.Helpers'
], function (
    Utils,
    Configuration,
    jQuery,
    LiveOrderModel,
    LiveOrderLineModel,
    InstrumentationAddToCart,
    Loggers,
    CartConfirmationHelpers
) {
    'use strict';

    // Check if running in real client browser (skip SSR / NetSuite SEO Page Generator so CDN won't cache Listrak tags)
    function isBrowserEnvironment(message) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return false;
        if (typeof SC !== 'undefined') {
            if (typeof SC.isPageGenerator === 'function' && SC.isPageGenerator()) return false;
            if (SC.ENVIRONMENT && SC.ENVIRONMENT.jsEnvironment && SC.ENVIRONMENT.jsEnvironment !== 'browser') return false;
        }
        if (navigator && navigator.userAgent && /Prerender|PhantomJS|HeadlessChrome/i.test(navigator.userAgent)) {
            return false;
        }
        // console.log('Is Browser!', message);
        return true;
    }

    // Clean stale cached scripts and prevent duplicate script.js execution
    let isShieldInitialized = false;
    function setupListrakCDNShield() {
        if (isShieldInitialized || typeof document === 'undefined') return;
        isShieldInitialized = true;

        // 0. Check if main Listrak script.js already exists in initial HTML DOM
        if (
            window._ltk ||
            typeof rejectScriptLoad !== 'undefined' ||
            document.getElementById('ltkSDK') ||
            document.querySelector('script[src*="listrakbi.com/scripts/script.js"]')
        ) {
            window.__ltkSDKInjected = true;
        }

        // Intercept appendChild & insertBefore so script.js can NEVER be appended twice asynchronously
        ['appendChild', 'insertBefore'].forEach((methodName) => {
            const origMethod = Node.prototype[methodName];
            if (typeof origMethod === 'function' && !Node.prototype[methodName].__ltkHooked) {
                const hookedMethod = function (newNode) {
                    if (newNode && newNode.nodeType === 1 && newNode.tagName === 'SCRIPT') {
                        const src = newNode.getAttribute('src') || newNode.src || '';
                        if (src.includes('listrakbi.com/scripts/script.js')) {
                            if (
                                window.__ltkSDKInjected ||
                                window._ltk ||
                                typeof rejectScriptLoad !== 'undefined' ||
                                document.getElementById('ltkSDK') ||
                                document.querySelector('script[src*="listrakbi.com/scripts/script.js"]')
                            ) {
                                console.warn('[Listrak CDN Shield] Blocked duplicate Listrak script.js injection:', src);
                                return newNode;
                            }
                            window.__ltkSDKInjected = true;
                        }
                    }
                    return origMethod.apply(this, arguments);
                };
                hookedMethod.__ltkHooked = true;
                Node.prototype[methodName] = hookedMethod;
            }
        });

        // Skip DOM script cleanup on checkout.ssp / my_account.ssp because they are never cached by CDN
        const isCheckoutOrMyAccount = /checkout\.ssp|my_account\.ssp/i.test(window.location.href);
        if (isCheckoutOrMyAccount) return;

        // 1. Neutralize ONLY stale s1.listrakbi.com getIds?callback=ltkCallbackXXXX JSONP scripts baked into cached HTML
        document.querySelectorAll('script[src*="s1.listrakbi.com"][src*="callback=ltkCallback"]').forEach((scriptEl) => {
            const src = scriptEl.getAttribute('src') || '';
            const match = src.match(/[?&]callback=(ltkCallback\d+)/);
            if (match && match[1] && typeof window[match[1]] !== 'function') {
                const staleCb = match[1];
                window[staleCb] = function () {
                    console.log(`[Listrak CDN Shield] Neutralized stale cached callback: ${staleCb}`);
                    delete window[staleCb];
                };
            }
            if (!scriptEl.dataset.ltkFresh) {
                scriptEl.remove();
            }
        });

        // 2. Watch ONLY direct children of <head> (zero impact on <body> rendering performance)
        if (typeof MutationObserver !== 'undefined' && document.head) {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
                            const src = node.getAttribute('src') || '';
                            if (src.includes('s1.listrakbi.com') && src.includes('callback=ltkCallback') && !node.dataset.ltkWatched) {
                                node.dataset.ltkWatched = 'true';

                                const match = src.match(/[?&]callback=(ltkCallback\d+)/);
                                if (match && match[1] && typeof window[match[1]] !== 'function') {
                                    const staleCb = match[1];
                                    window[staleCb] = function () {
                                        console.log(`[Listrak CDN Shield] Neutralized stale cached callback: ${staleCb}`);
                                        delete window[staleCb];
                                    };
                                }

                                const cleanUpTag = () => {
                                    setTimeout(() => {
                                        console.log(`[Listrak CDN Shield] Executed & removed transient JSONP tag from DOM: ${src}`);
                                        node.remove();
                                    }, 0);
                                };
                                node.addEventListener('load', cleanUpTag, { once: true });
                                node.addEventListener('error', cleanUpTag, { once: true });
                            }
                        }
                    }
                }
            });
            observer.observe(document.head, { childList: true });
        }
    }
    if (isBrowserEnvironment('setupListrakCDNShield')) setupListrakCDNShield();

    // Load Framework
    function loadScript(merchantID) {
        if (!isBrowserEnvironment('loadScript')) return;
        setupListrakCDNShield();

        try {
            console.log("Listrak Javascript Framework was loaded");
            // Listrak Analytics - Javascript Framework
            (function (d, tid, vid) {
                if (
                    window.__ltkSDKInjected ||
                    window._ltk ||
                    typeof rejectScriptLoad !== 'undefined' ||
                    d.getElementById('ltkSDK') ||
                    d.querySelector('script[src*="listrakbi.com/scripts/script.js"]')
                ) {
                    console.log('[Listrak CDN Shield] script.js already present or loading, skipping duplicate load.');
                    return;
                }
                var js = d.createElement('script');
                js.id = 'ltkSDK';
                js.async = true;
                js.dataset.ltkFresh = 'true';
                js.src = "https://cdn.listrakbi.com/scripts/script.js?m=" + tid + "&v=" + vid;
                d.querySelector('head').appendChild(js);
            })(document, merchantID, '1');

            // Listrak Merchandise Blocks
            (function(d){
                if(typeof _ltk=="undefined"){if(document.addEventListener)document.addEventListener("ltkAsyncListener",function(){_ltk_util.ready(d)});else{var e=document.documentElement;e.ltkAsyncProperty=0;e.attachEvent("onpropertychange",function(e){if(e.propertyName=="ltkAsyncProperty"){_ltk_util.ready(d)}})}}else{_ltk_util.ready(d)}})(function(){
                /********** Begin Custom Code **********/
                //region 2.0 Ensure Render runs asynchronously after current call stack and after Listrak script is ready
                // Option A: macrotask (very compatible)
                setTimeout(function() {
                    if (window._ltk && _ltk.Recommender && typeof _ltk.Recommender.Render === 'function') {
                        try {
                            _ltk.Recommender.Render();
                            window._lkt_Recommender = true;
                            // console.log('_ltk.Recommender.Render() executed');
                        } catch (err) {
                            console.error('Listrak Recommender.Render error', err);
                        }
                    } else {
                        console.warn('Listrak _ltk.Recommender not available yet.');
                    }
                }, 0);

                // Option B: microtask (ES6+)
                // Promise.resolve().then(function() {
                //     if (window._ltk && _ltk.Recommender && typeof _ltk.Recommender.Render === 'function') {
                //         _ltk.Recommender.Render();
                //         window._lkt_Recommender = true;
                //     }
                // });
                //endregion 2.0 Ensure Render runs asynchronously after current call stack and after Listrak script is ready
                /********** End Custom Code **********/
            });
        } catch (err) {
            console.error('Err: ', err);
        }
    }

    // Email Capture
    function loadEmailCapture(emailEleId) {
        if (!isBrowserEnvironment('loadEmailCapture')) return;

        try {
            // Listrak Analytics - Email Capture
            (function(){if(typeof _ltk == 'object'){ltkCode();}else{(function (d) { if (document.addEventListener)
                document.addEventListener('ltkAsyncListener', d); else { var e = document.documentElement; e.ltkAsyncProperty = 0;
                e.attachEvent('onpropertychange', function (e) { if (e.propertyName == 'ltkAsyncProperty') { d(); } }); }
                })(function(){ltkCode();});}function ltkCode(){_ltk_util.ready(function(){
                /********** Begin Custom Code **********/
                setTimeout(function() {
                    if (window._ltk && _ltk.SCA && typeof _ltk.SCA.CaptureEmail === 'function') {
                        try {
                            _ltk.SCA.CaptureEmail(emailEleId);
                            // console.log(`_ltk.SCA.CaptureEmail("${emailEleId}")`);
                        } catch (err) {
                            console.error('Listrak _ltk.SCA.CaptureEmail error', err);
                        }
                    } else {
                        console.warn('Listrak _ltk.SCA.CaptureEmail not available yet.');
                    }
                }, 0);
                /********** End Custom Code ************/
            })}})();
        } catch (err) {
            console.error('Err: ', err);
        }
    }

    // Browse Abandonment:
    // Browsing a Page
    function browseAbandonmentPage() {
        if (!isBrowserEnvironment('browseAbandonmentPage')) return;

        try {
            (function(){if(typeof _ltk == 'object'){ltkCode();}else{(function (d) { if (document.addEventListener)
                document.addEventListener('ltkAsyncListener', d); else { var e = document.documentElement; e.ltkAsyncProperty = 0;
                e.attachEvent('onpropertychange', function (e) { if (e.propertyName == 'ltkAsyncProperty') { d(); } }); }
            })(function(){ltkCode();});}function ltkCode(){_ltk_util.ready(function(){
                /********** Begin Custom Code **********/

                setTimeout(function() {
                    if (window._ltk && _ltk.Activity && typeof _ltk.Activity.AddPageBrowse === 'function') {
                        try {
                            _ltk.Activity.AddPageBrowse(window.location.href);
                            // console.log('_ltk.Activity.AddPageBrowse(' + window.location.href + ')');
                        } catch (err) {
                            console.error('Listrak _ltk.Activity.AddPageBrowse error', err);
                        }
                    } else {
                        console.warn('Listrak _ltk.Activity.AddPageBrowse not available yet.');
                    }
                }, 0);

                /********** End Custom Code ************/
            })}})();
        } catch (err) {
            console.error('Err: ', err);
        }
    }

    // Browsing a Product
    function browseAbandonmentProd(sku) {
        if (!isBrowserEnvironment('browseAbandonmentProd')) return;

        try {
            if (!sku)
                return;

            (function(){if(typeof _ltk == 'object'){ltkCode();}else{(function (d) { if (document.addEventListener)
                document.addEventListener('ltkAsyncListener', d); else { var e = document.documentElement; e.ltkAsyncProperty = 0;
                e.attachEvent('onpropertychange', function (e) { if (e.propertyName == 'ltkAsyncProperty') { d(); } }); }
            })(function(){ltkCode();});}function ltkCode(){_ltk_util.ready(function(){
                /********** Begin Custom Code **********/

                setTimeout(function() {
                    if (window._ltk && _ltk.Activity && typeof _ltk.Activity.AddProductBrowse === 'function') {
                        try {
                            _ltk.Activity.AddProductBrowse(sku);
                            // console.log('_ltk.Activity.AddProductBrowse(' + sku + ')');
                        } catch (err) {
                            console.error('Listrak _ltk.Activity.AddProductBrowse error', err);
                        }
                    } else {
                        console.warn('Listrak _ltk.Activity.AddProductBrowse not available yet.');
                    }
                }, 0);

                /********** End Custom Code ************/
            })}})();
        } catch (err) {
            console.error('Err: ', err);
        }
    }

    // Cart Abandonment
    function sendCartToListrak() {
        if (!isBrowserEnvironment('sendCartToListrak')) return;

        (function(){if(typeof _ltk == 'object'){ltkCode();}else{(function (d) { if (document.addEventListener)
            document.addEventListener('ltkAsyncListener', d); else { var e = document.documentElement; e.ltkAsyncProperty = 0;
            e.attachEvent('onpropertychange', function (e) { if (e.propertyName == 'ltkAsyncProperty') { d(); } }); }
        })(function(){ltkCode();});}function ltkCode(){_ltk_util.ready(function(){
            /********** Begin Custom Code **********/

            var collections = _.filter(LiveOrderModel.getInstance().get('lines').models || [], function(line) {
                return !0 !== line.get("free_gift")
            }) || [];

            if (collections.length > 0) {
                try {
                    for (var index = 0; index < collections.length; index++) {
                        var element = collections[index];

                        var sku = element.getSku();
                        var quantity = element.attributes.quantity;
                        var price = element.getPrice().price;
                        var title = element.getItem().get('_name');
                        var imageURL = element.getThumbnail().url;
                        var productURL = element.generateURL();
                        if (productURL.indexOf("/") === 0)
                            productURL = productURL.replace(/^\/+/g, '');

                        _ltk.SCA.AddItemWithLinks(sku, quantity, price, title, imageURL, productURL);
                        // console.log("_ltk.SCA.AddItemWithLinks("+ sku +", "+ quantity +", "+ price +", "+ title +", "+ imageURL +", "+ productURL +")");
                    }
                    // console.log('_ltk.SCA.Submit()');
                    _ltk.SCA.Submit();

                    // console.log("Cart had been sent to Listrak!");
                } catch (error) {
                    console.error(error);
                }
            } else {
                _ltk.SCA.ClearCart();
                // console.log('_ltk.SCA.ClearCart()');
                // console.log("Cart was cleared from Listrak!");
            }

            /********** End Custom Code ************/
        })}})();
    }

    function addToCart(e, _this) {
        try {
            setTimeout(function() {

                for (var i = 0; i < document.getElementsByClassName("bx-pager-link").length; i++) {

                    var activeImgOptionURL = (document.getElementsByClassName("bx-pager-link")[i].children[0]).currentSrc.split("?")[0];
                    var mainImg = document.getElementsByClassName("product-details-image-gallery-container")[i+1].innerHTML;
                    var mainImgSplit = mainImg.split(" ");
                    var mainImgSplitURL = mainImgSplit[1].split("?");

                    if(mainImgSplitURL[0] != ("src=\"" + activeImgOptionURL)){

                        mainImgSplitURL[0] = "src=" + activeImgOptionURL;
                        mainImgSplit[1] = mainImgSplitURL.join("?");
                        var newImgSrc = mainImgSplit.join(" ");
                        document.getElementsByClassName("product-details-image-gallery-container")[i+1].innerHTML = newImgSrc;

                    }
                }
            }, 3000);
        } catch (e) {
            console.warn('The Zoom bug fix is throwing an error (Cart.AddToCart.Button.View): ' + JSON.stringify(e));
        }

        e.preventDefault();
        const self = _this;
        var cart_promise;

        if (
            !_this.model.areAttributesValid(['options', 'quantity'], self.getAddToCartValidators())
        ) {
            return;
        }

        if (!_this.model.isNew() && _this.model.get('source') === 'cart') {
            cart_promise = _this.cart.updateProduct(_this.model);
            cart_promise.done(function() {
                self.options.application.getLayout().closeModal();

                /**
                 * Listrak Customization
                 * START
                 */
                // console.log("_this.cart: ", _this.cart.get('lines').models);
                sendCartToListrak();
                /**
                 * Listrak Customization
                 * END
                 */
            });
        } else {
            var line = LiveOrderLineModel.createFromProduct(_this.model);
            var itemTrack = InstrumentationAddToCart.itemToTrack(line);
            var loggers = Loggers.Loggers.getLogger();
            var actionId = loggers.start('Add to Cart');
            cart_promise = _this.cart.addLine(line);
            cart_promise.then(() => {
                var addToCartOperationIds = InstrumentationAddToCart.getAddToCartOperationId(
                    _this.cart.get('lines').models,
                    line
                );
                loggers.end(actionId, {
                    operationIds: addToCartOperationIds,
                    status: 'success',
                    itemId: itemTrack.itemId,
                    itemQuantity: itemTrack.itemQuantity
                });

                /**
                 * Listrak Customization
                 * START
                 */
                // console.log("_this.cart: ", _this.cart.get('lines').models);
                sendCartToListrak();
                /**
                 * Listrak Customization
                 * END
                 */
            });

            CartConfirmationHelpers.showCartConfirmation(
                cart_promise,
                line,
                self.options.application
            );
        }

        cart_promise.fail(function(jqXhr) {
            var error_details = null;
            try {
                if (jqXhr && jqXhr.responseText) {
                    var response = JSON.parse(jqXhr.responseText);
                    error_details = response.errorDetails;
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (error_details && error_details.status === 'LINE_ROLLBACK') {
                    self.model.set('internalid', error_details.newLineId);
                }
            }
        });

        _this.disableElementsOnPromise(cart_promise, e.target);

        return false;
    }

    function removeItem(e, _this) {
        const self = _this;
        const product = _this.model.get('lines').get(_this.$(e.target).data('internalid'));
        const remove_promise = _this.model.removeLine(product);
        const internalid = product.get('internalid');

        _this.isRemoving = true;
        _this.disableElementsOnPromise(
            remove_promise,
            `article[id="${internalid}"] a, article[id="${internalid}"] button`
        );

        remove_promise.always(function() {
            self.isRemoving = false;
            /**
             * Listrak Customization
             * START
             */
            sendCartToListrak();
            /**
             * Listrak Customization
             * END
             */
        });

        return remove_promise;
    }

    function updateItemQuantity(e, _this) {
        const self = _this;
        const $form = _this.getItemForm(e);
        const options = ($form).serializeObject();
        let { internalid } = options;
        let line = _this.model.get('lines').get(internalid);
        const $input = $form.find('[name="quantity"]');
        const validInput = _this.validInputValue($input[0]);

        if (!line || _this.isRemoving) {
            return;
        }

        if (!validInput || options.quantity) {
            const new_quantity = parseInt(options.quantity, 10);
            const item = line.get('item');
            const min_quantity = item
                ? item.get('_minimumQuantity', true)
                : line.get('minimumquantity');
            const max_quantity = item
                ? item.get('_maximumQuantity', true)
                : line.get('maximumquantity');
            const current_quantity = parseInt(line.get('quantity'), 10);

            let new_valid_quantity = new_quantity >= min_quantity ? new_quantity : min_quantity;
            new_valid_quantity =
                !!max_quantity && new_quantity > max_quantity
                    ? max_quantity
                    : new_quantity > min_quantity
                        ? new_quantity
                        : min_quantity;

            $input.val(new_valid_quantity);

            if (new_quantity !== current_quantity) {
                $input.val(new_valid_quantity).prop('disabled', true);
                line.set('quantity', new_valid_quantity);

                const invalid = line.validate();

                if (!invalid) {
                    const update_promise = _this.model.updateLine(line, true);
                    _this.disableElementsOnPromise(update_promise, `#${internalid} button`);

                    update_promise
                        /**
                         * Listrak Customization
                         * START
                         */
                        .done(function(result){
                            // console.log(result);
                            sendCartToListrak();
                        })
                        /**
                         * Listrak Customization
                         * END
                         */
                        .fail(function(jqXhr) {
                            const result = JSON.parse(jqXhr.responseText);
                            const { errorDetails } = result;

                            if (errorDetails.newLineId) {
                                internalid = errorDetails.newLineId;
                                // we use the new line
                                line = self.model.get('lines').get(internalid);
                            }

                            // revert quantity to the last quantity
                            line.set('quantity', current_quantity);
                            self.render();
                        })
                        .always(function() {
                            $input.prop('disabled', false);
                        });
                } else {
                    const placeholder = _this.$(`#${internalid} [data-type="alert-placeholder"]`);
                    placeholder.empty();

                    _.each(invalid, function(value) {
                        const global_view_message = new GlobalViewsMessageView({
                            message: value,
                            type: 'error',
                            closable: true
                        });

                        placeholder.append(global_view_message.render().$el.html());
                    });

                    $input.prop('disabled', false);
                    line.set('quantity', current_quantity);
                }
            }
        }
    }

    return {
        isBrowserEnvironment: isBrowserEnvironment,
        loadScript: loadScript,
        loadEmailCapture: loadEmailCapture,
        browseAbandonmentPage: browseAbandonmentPage,
        browseAbandonmentProd: browseAbandonmentProd,
        sendCartToListrak: sendCartToListrak,

        // Default behaviour
        addToCart: addToCart,
        removeItem: removeItem,
        updateItemQuantity: updateItemQuantity,
    };
});
